import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, computed, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipDirective } from 'shared/directives/tooltip.directive';

/**
 * @description
 * The **GaugeComponent** displays either a semi-circular gauge (default) or a full circular gauge
 * depending on the `isCircleMode` input. The gauge rotates/fills proportionally to the progress value.
 * It can color itself differently based on the current progress, indicating thresholds like
 * *gray*, *danger*, *warning*, *success*, or *primary* (full). An internal animation is used to smoothly transition
 * between values, with the duration proportional to the target percentage.
 *
 * @param percentage
 * The numeric progress value (0–100) to display as the gauge’s fill level.
 *
 * @param isCircleMode
 * When true, renders a full circular progress ring instead of the default semi-circular gauge.
 *
 * @example
 * ```html
 * <app-gauge [percentage]="75"></app-gauge>
 * <app-gauge [percentage]="75" [isCircleMode]="true"></app-gauge>
 * ```
 */

const CIRCLE_RADIUS = 32;
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

@Component({
    selector: 'app-gauge',
    imports: [CommonModule, TooltipDirective, TranslateModule],
    templateUrl: './gauge.component.html',
    styleUrls: ['./gauge.component.scss'],
})
export class GaugeComponent implements OnChanges {
    @Input() percentage: number = 0;
    isCircleMode = input<boolean>(false);

    private animatedValue = signal(0);
    animatedPercentage = computed(() => this.animatedValue());
    animationDuration = signal(0);

    readonly circleRadius = CIRCLE_RADIUS;
    readonly circleCircumference = CIRCLE_CIRCUMFERENCE;

    gaugeStyle = computed(() => ({
        transform: this.gaugeRotation(),
        'transition-duration': `${this.animationDuration()}ms`,
        'transition-property': 'transform',
        'transition-timing-function': 'cubic-bezier(0.4, 0.0, 0.2, 1)',
    }));

    // Computes the rotation based on the animated progress value.
    // At 0%: -0.5turn → -180°; at 50%: -0.25turn → -90°; at 100%: 0turn → 0°
    gaugeRotation = computed(() => {
        return `rotate(calc(-0.5turn + ${this.animatedPercentage()} * 0.005turn))`;
    });

    circleStrokeStyle = computed(() => {
        const progressFraction = this.animatedPercentage() / 100;
        const strokeDashoffset = CIRCLE_CIRCUMFERENCE * (1 - progressFraction);
        return {
            'stroke-dasharray': `${CIRCLE_CIRCUMFERENCE}`,
            'stroke-dashoffset': `${strokeDashoffset}`,
            'transition-duration': `${this.animationDuration()}ms`,
            'transition-property': 'stroke-dashoffset',
            'transition-timing-function': 'cubic-bezier(0.4, 0.0, 0.2, 1)',
        };
    });

    progressClass = computed(() => {
        const value = this.animatedPercentage();
        switch (true) {
            case value < 20:
                return 'progress-danger';
            case value < 40:
                return 'progress-warningDark';
            case value < 60:
                return 'progress-warningLight';
            case value < 80:
                return 'progress-primary';
            default:
                return 'progress-success';
        }
    });

    gradientStartColor = computed(() => {
        const value = this.animatedPercentage();
        switch (true) {
            case value < 20:
                return 'var(--color-danger-400)';
            case value < 40:
                return 'var(--color-warning-500)';
            case value < 60:
                return 'var(--color-warning-300)';
            case value < 80:
                return 'var(--color-primary-500)';
            default:
                return 'var(--color-success-400)';
        }
    });

    gradientEndColor = computed(() => {
        const value = this.animatedPercentage();
        switch (true) {
            case value < 20:
                return 'var(--color-danger-600)';
            case value < 40:
                return 'var(--color-warning-700)';
            case value < 60:
                return 'var(--color-warning-400)';
            case value < 80:
                return 'var(--color-primary-600)';
            default:
                return 'var(--color-success-600)';
        }
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['percentage']) {
            const newPercentage = this.clamp(changes['percentage'].currentValue);
            const duration = this.computeDuration(newPercentage);
            this.animationDuration.set(duration);
            this.animateGauge(newPercentage);
        }
    }

    private animateGauge(finalValue: number) {
        const startValue = this.animatedValue();
        const steps = 100;
        const duration = this.computeDuration(finalValue);
        const stepTime = duration / steps;
        const increment = (finalValue - startValue) / steps;
        let currentStep = 0;

        const step = () => {
            currentStep++;
            let newValue = startValue + increment * currentStep;
            if ((increment >= 0 && newValue > finalValue) || (increment < 0 && newValue < finalValue)) {
                newValue = finalValue;
            }
            this.animatedValue.set(Math.round(newValue));
            if (currentStep < steps) {
                setTimeout(step, stepTime);
            }
        };

        // If the timeout is not delayed by 0ms, the transition does not apply correctly
        // Timeout OK
        setTimeout(() => {
            step();
        }, 0);
    }

    private computeDuration(finalValue: number): number {
        // Clamp the input between 1 and 100
        if (finalValue < 1) finalValue = 1;
        if (finalValue > 100) finalValue = 100;
        // Need a linear map from [1..100%] to [200..400ms].
        const slope = 200 / 99;
        const intercept = 200 - slope;
        return Math.round(slope * finalValue + intercept);
    }

    private clamp(value: number): number {
        return Math.min(100, Math.max(0, value));
    }
}
